/**
 * Topics service API calls.
 *
 * This module provides functions for topic cluster management including
 * fetching ranked topics, topic details, and generating new topics.
 */

import apiClient from './api';

/**
 * Get ranked topic clusters for dashboard
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Maximum number of topics to return (default: 12, max: 50)
 * @param {boolean} params.unread_only - Whether to score topics based only on unread articles
 * @returns {Promise<Object>} Ranked topics response
 */
export const getTopics = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      limit: params.limit || 12,
      unread_only: params.unread_only !== undefined ? params.unread_only : true,
    });

    const response = await apiClient.get(`/api/topics/?${queryParams}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to fetch topics'
    );
  }
};

/**
 * Get detailed information about a specific topic
 * @param {string} topicId - Topic ID (UUID)
 * @param {Object} params - Query parameters
 * @param {boolean} params.unread_only - Whether to show only unread articles
 * @param {number} params.limit - Maximum number of articles to return (default: 20)
 * @param {number} params.offset - Number of articles to skip (default: 0)
 * @returns {Promise<Object>} Topic details with articles
 */
export const getTopicDetails = async (topicId, params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      unread_only: params.unread_only !== undefined ? params.unread_only : false,
      limit: params.limit || 20,
      offset: params.offset || 0,
    });

    const response = await apiClient.get(`/api/topics/${topicId}?${queryParams}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to fetch topic details'
    );
  }
};

/**
 * Generate new topic clusters for user's articles
 * @param {Object} params - Generation parameters
 * @param {boolean} params.unread_only - Whether to generate topics for unread articles only
 * @param {boolean} params.force_regenerate - Force regeneration even if topics exist
 * @returns {Promise<Object>} Generation result
 */
export const generateTopics = async (params = {}) => {
  try {
    const requestData = {
      unread_only: params.unread_only !== undefined ? params.unread_only : true,
      force_regenerate: params.force_regenerate || false,
    };

    const response = await apiClient.post('/api/topics/generate', requestData);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to generate topics'
    );
  }
};

/**
 * Delete a specific topic cluster
 * @param {string} topicId - Topic ID (UUID) to delete
 * @returns {Promise<Object>} Deletion result
 */
export const deleteTopic = async (topicId) => {
  try {
    const response = await apiClient.delete(`/api/topics/${topicId}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to delete topic'
    );
  }
};

/**
 * Delete all topic clusters for current user
 * @returns {Promise<Object>} Deletion result
 */
export const deleteAllTopics = async () => {
  try {
    const response = await apiClient.delete('/api/topics/');
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to delete all topics'
    );
  }
};